// ============================================================
// attack/victim - ONE VICTIM, ALL THE WAY THROUGH: their damage, then
// their reaction.
//
// The invariant that makes an area attack correct: the to-hit roll and
// everything derived from it - DoS, hit count, jams, Devastating,
// RazorSharp - was decided once, at the shape (`attack/shot`). Everything
// from the hit location onward is recomputed HERE, against this victim.
// That is how Graviton reads their armour and not somebody else's, how
// Melta and Scatter read their range band, and how Felling meets their
// toughness.
//
// ROLL ORDER CONTRACT, per victim: the avoid test's d100 (when the block
// names one), then every die `resolveHitsAgainst` draws.
// ============================================================
import { helplessAgainst, isHelpless } from '../../engine/helpless';
import { isUnaware } from '../../engine/unaware';
import { AVOID_TEST_LABEL, avoidBaseOf, type ResolvedArea, type ResolvedSpawn } from '../../engine/areaShapes';
import { onHitExposure, reactionExposure } from '../../engine/exposures';
import { HIT_LOCATIONS } from '../../engine/constants';
import { reactionsAgainst, resolveReactionAvailability } from '../../engine/reactionAvailability';
import { attackerDefenceMods } from '../../engine/defenceChain';
import { withDefenderMods } from '../../engine/defenceCalc';
import { resolveHitsAgainst, type ResolveHitsParams } from '../../engine/attackResolution';
import type { HitResult } from '../../engine/damageCalc';
import deepClone from '../../utils/deepClone';
import { rollD100 } from '../../engine/index';
import type { Target } from '../../types';
import type { ApplyHitsData } from '../useApplyHits';
import { actorReader, type AttackContext } from './context';
import { reactionModClause, victimNotes } from './victimNotes';
import type { Shot } from './shot';
import type { AttackVictim } from './types';
import type { BeatKindFacts } from './beatFacts';

/** The volley a victim belongs to: null for a single-target attack, and how many it caught. */
export interface Volley {
  id: string | null;
  size: number;
}

/** One victim, framed: whose template caught them, and what their log line starts with. */
interface VictimFrame {
  victim: AttackVictim;
  /** The secondary template, when that is what caught them (v8.373.2). */
  spawnedBlock: ResolvedSpawn | null;
  /** The template every block rule is read off - the weapon's own, or the secondary. */
  area: ResolvedArea | ResolvedSpawn;
  /** The aimed victim of the aimed shot: Accurate, the countdown and the attacker's prompt are theirs alone. */
  isPrimary: boolean;
  target: Target;
  band: string;
  /** Hits dealt to them - a suppression burst's share, else the whole volley. */
  hits: number;
  /** Which leap of an arc (0 = not an echo). */
  leap: number;
  notes: string[];
  /** A `hitLocation` exposure on THIS victim (v8.368.5): every uncalled hit lands there. */
  exposedLocation: string | null;
}

/** "✸ Spawned from Brother Aurel: the spray catches Ork." */
function spawnedNote(ctx: AttackContext, victim: AttackVictim, block: ResolvedSpawn, victimName: string): string {
  const spawner = ctx.tabs.find((t) => t.id === victim.spawnedFrom)?.target?.name || victim.spawnedFrom;
  const shape = block.shape === 'cone' ? 'spray' : block.shape;
  return '✸ Spawned from ' + spawner + ': the ' + shape + ' catches ' + victimName + '.';
}

/** An echo's lines (v8.372.4): which leap reached them, and whether they may still react. */
function echoNotes(ctx: AttackContext, f: Omit<VictimFrame, 'notes' | 'exposedLocation'>): string[] {
  const victimName = f.target.name || f.victim.tabId;
  const out: string[] = [];
  if (f.spawnedBlock) out.push(spawnedNote(ctx, f.victim, f.spawnedBlock, victimName));
  else if (f.leap > 0) out.push('⚡ Leap ' + f.leap + ': the arc reaches ' + victimName + '.');
  if (f.leap > 0 && !f.area.echoesReact)
    out.push(
      f.spawnedBlock
        ? 'Spawned: no reaction - it is already on them.'
        : 'Echo: no reaction - the lightning is already on them.',
    );
  return out;
}

/** Where a `hitLocation` exposure sends every uncalled hit, or null. */
function exposedLocationOf(ctx: AttackContext, isMelee: boolean, target: Target): string | null {
  if (ctx.targetLocation !== 'auto') return null;
  const bearer = {
    conditions: target.conditions,
    exposures: target.exposures,
    teamTag: target.teamTag,
    sideTag: target.sideTag,
  };
  return onHitExposure(bearer, actorReader(ctx, isMelee)).hitLocation ?? null;
}

/** "Exposed: every hit lands on the body." - and says so (v8.368.9). */
function exposedLocationNote(location: string): string {
  const named = HIT_LOCATIONS.find((l) => l.id === location)?.name ?? location;
  return `Exposed: every hit lands on the ${named.toLowerCase()}.`;
}

/** Frame a victim, or null when there is no target to resolve against. */
function frameVictim(ctx: AttackContext, shot: Shot, victim: AttackVictim): VictimFrame | null {
  const spawnedBlock = victim.spawnedFrom ? (shot.rolledArea.spawn ?? null) : null;
  const target = victim.target || ctx.tabs.find((t) => t.id === victim.tabId)?.target;
  if (!target) return null;
  const base = {
    victim,
    spawnedBlock,
    area: spawnedBlock ?? shot.rolledArea,
    // The aimed combatant caught by a SPAWNED copy is no longer the aimed shot.
    isPrimary: victim.tabId === shot.tabId && !spawnedBlock,
    target,
    band: victim.rangeBand || ctx.rangeBand,
    // A suppression burst scatters (v8.307.0): each victim carries the share the dice dealt them.
    hits: victim.hitCount ?? shot.numHits,
    leap: victim.leap ?? 0,
  };
  const exposedLocation = exposedLocationOf(ctx, shot.isMelee, target);
  const notes = [...shot.notes, ...victimNotes(shot.sp, target, base.area.coverBypass), ...echoNotes(ctx, base)];
  if (exposedLocation) notes.push(exposedLocationNote(exposedLocation));
  return { ...base, notes, exposedLocation };
}

const PASS_VERDICT: Record<string, string> = { half: ' - half damage', quarter: ' - a quarter of the damage' };
const PASS_SCALE: Record<string, number> = { half: 0.5, quarter: 0.25 };

/**
 * SPRAY IS AVOIDED, NOT DODGED.
 *
 * A flamer auto-hits, and the escape is a test rather than a reaction: it
 * costs nothing, it is not refused to the Immobilised or the Frenzied, and
 * it does not queue as a prompt. Rolled here, once per victim, through the
 * dice system - anything else shifts every seeded sequence after it
 * (gotcha #5). The AREA BLOCK (v8.369.1) names the test; no test, no die.
 *
 * Returns the damage scale a pass buys (1 when caught or untested), or
 * null when they stepped clear altogether.
 */
function rollAvoid(ctx: AttackContext, shot: Shot, f: VictimFrame, volley: Volley): number | null {
  const test = f.area.avoid;
  if (!test) return 1;
  const base = avoidBaseOf(f.target, test) + (f.area.avoidMod || 0);
  const roll = rollD100();
  const avoided = roll <= base;
  // What a pass buys (v8.372.4): stepping clear, or the raw roll halved or quartered.
  const outcome = f.area.avoidOutcome || 'avoided';
  const verdict = avoided ? (PASS_VERDICT[outcome] ?? ' - avoided') : ' - caught in it';
  const shape = f.area.shape === 'cone' ? 'spray' : f.area.shape;
  const who = f.target.name || f.victim.tabId;
  ctx.addNarrative(f.victim.tabId, {
    type: 'info',
    text: who + ' tests ' + AVOID_TEST_LABEL[test] + ' vs the ' + shape + ': ' + roll + ' vs ' + base + verdict,
    timestamp: Date.now(),
    // PART OF THE VOLLEY, especially when they avoid it: someone who passes
    // writes no attack entry, so without the volley on THIS line a cone
    // everybody dodges makes no block at all.
    volleyId: volley.id,
    volleySize: volley.size,
    // So the block's header can name the weapon, not "Area attack".
    slotName: shot.slot.weaponName,
  });
  if (!avoided) return 1;
  return outcome === 'avoided' ? null : (PASS_SCALE[outcome] ?? 1);
}

/** What `resolveHitsAgainst` needs for this victim. Declarative: one field per input. */
function hitsRequest(ctx: AttackContext, shot: Shot, f: VictimFrame, avoidScale: number): ResolveHitsParams {
  const stats = ctx.attackerCharStats;
  return {
    slot: shot.slot,
    target: f.target,
    numHits: f.hits,
    dos: shot.dos,
    pen: shot.pen + shot.hammerBlow,
    // ACCURATE IS AIMED: it belongs to the victim the attacker was pointing at.
    accurateBonus: f.isPrimary ? shot.accurateBonus : 0,
    rangeBand: f.band,
    isMelee: shot.isMelee,
    isShortOrCloser: f.band === 'pointBlank' || f.band === 'short',
    targetLocation: f.exposedLocation ?? ctx.targetLocation,
    attackRoll: shot.attackRoll,
    // The countdown was the attacked combatant's; the others are not regenerating on account of being shelled.
    regenHealed: f.isPrimary ? shot.regenHealed : 0,
    hatred: ctx.hatred,
    // Deathwatch Training auto-confirms RF vs Xenos-flagged targets (v6.29.0)
    autoConfirmRF: (stats?.hasDeathwatchTraining ?? false) && !!f.target.xenos,
    damageBonusMelee: (stats?.damageBonusMelee ?? 0) + shot.hammerBlow,
    damageBonusRanged: stats?.damageBonusRanged ?? 0,
    attackerTeamTag: ctx.attackerTeamTag,
    attackerSideTag: ctx.attackerSideTag,
    rerollDamage: ctx.rerollDamage,
    helpless: helplessAgainst(f.target, shot.isMelee, ctx.condModOptions),
    protagonistRules: ctx.protagonistRules,
    averagedDamage: ctx.averagedDamage,
    ...blockRiders(f, avoidScale),
  };
}

/** An echo's falloff and crit rule (v8.372.4), a passed avoid test's scale, a spawned block's cover rule (v8.373.2). */
function blockRiders(f: VictimFrame, avoidScale: number): Partial<ResolveHitsParams> {
  return {
    ...(f.leap > 0 ? { falloff: { ...f.area.falloff, leap: f.leap }, echoesCrit: f.area.echoesCrit } : {}),
    ...(avoidScale < 1 ? { rawScale: avoidScale } : {}),
    ...(f.spawnedBlock ? { coverBypass: f.spawnedBlock.coverBypass } : {}),
  };
}

/** Has the attacker won a Feint? Read from this render's context - consuming it is a later state write. */
function hasFeinted(ctx: AttackContext, isMelee: boolean): boolean {
  return isMelee && (ctx.actorConditionContext?.conditions ?? []).some((c: string) => c === 'Ft' || c.startsWith('Ft'));
}

type ReactionMods = { dodge: number; parry: number } | null;

/**
 * What the ATTACKER wears that changes how this victim reacts (v8.368.3):
 * Whirling is hard to parry, Lumbering easy to dodge. Read once here,
 * carried on the prompt and folded into the frozen target, so the banner,
 * the first step, the chain and the forecast all read one number.
 */
function attackerReactionMods(ctx: AttackContext, isMelee: boolean, f: VictimFrame): ReactionMods {
  const actor = ctx.actorConditionContext;
  const reaction = reactionExposure(
    {
      conditions: (actor?.conditions as string[] | undefined) ?? [],
      exposures: (actor?.exposures as string[] | undefined) ?? [],
      teamTag: ctx.attackerTeamTag,
      sideTag: ctx.attackerSideTag,
    },
    { teamTag: f.target.teamTag ?? null, sideTag: f.target.sideTag ?? null, isMelee },
  );
  if (!reaction.dodgeMod && !reaction.parryMod) return null;
  const clause = reactionModClause(reaction.dodgeMod, reaction.parryMod);
  for (const label of reaction.labels) f.notes.push(`${label}: ${clause} against their attacks.`);
  return { dodge: reaction.dodgeMod, parry: reaction.parryMod };
}

interface ReactionGate {
  /** Somewhere to put a prompt, and a victim the block lets react. */
  canPrompt: boolean;
  /** A Dodge or a Parry is open to them. */
  canReact: boolean;
  mods: ReactionMods;
}

/**
 * May this victim react, and can a prompt be raised?
 *
 * Frenzy, Battle Rage AND the condition gates are one call, the same one the
 * defence chain makes. A REACTION REQUIRES SOMEWHERE TO PUT IT: the aimed
 * victim always has `pendingAttack`; anybody else needs a pending-defence
 * store, or their hits are applied rather than dropped (v8.154.0). An echo
 * the block denies a reaction goes straight to the hits (v8.372.4).
 */
/**
 * Unaware or helpless: no reaction, WHATEVER the condition-modifiers rule
 * says - you cannot dodge what you have not noticed, or while you cannot
 * move. Returns the word for the log, or null.
 */
function caughtOut(ctx: AttackContext, f: VictimFrame): string | null {
  if (isHelpless(f.target)) return 'Helpless';
  return isUnaware(f.target, ctx.unawareIds?.has(f.victim.tabId)) ? 'Unaware' : null;
}

/** What stops this victim reacting although their conditions allow it: a won feint, or being caught out. Notes it. */
function reactionDenied(ctx: AttackContext, shot: Shot, f: VictimFrame): boolean {
  // A won Feint promises this melee attack cannot be avoided.
  if (hasFeinted(ctx, shot.isMelee)) {
    f.notes.push('Feinted - this attack cannot be Dodged or Parried.');
    return true;
  }
  const caught = caughtOut(ctx, f);
  if (caught) f.notes.push(`${caught} - no Dodge, no Parry.`);
  return !!caught;
}

/**
 * Could this victim take any reaction against THIS attack - the rule the prompt offers by (reactionsAgainst): a
 * parry only meets a blade, and the weapon's own denials count. Asked of the sheet alone, a shot at a defender who
 * could only parry prompted for "No Defence" (2026-10-30).
 */
function couldReact(ctx: AttackContext, shot: Shot, f: VictimFrame): boolean {
  const availability = resolveReactionAvailability(f.target, {
    applyConditionMods: ctx.applyConditionModifiers,
    condOptions: ctx.condModOptions,
  });
  const denial = attackerDefenceMods(shot.slot?.specialRules, f.target.parry?.parryWeaponSpecials);
  const attack = { isMelee: shot.isMelee, canParryRanged: !!f.target.parry?.canParryRanged };
  const { canDodge, canParry } = reactionsAgainst(availability, denial, attack);
  return canDodge || canParry;
}

function reactionGate(ctx: AttackContext, shot: Shot, f: VictimFrame): ReactionGate {
  const could = couldReact(ctx, shot, f);
  return {
    canPrompt: (f.isPrimary || !!ctx.createPendingDefence) && (f.leap === 0 || f.area.echoesReact),
    // Asked only of someone who could have reacted: nobody is told a feint beat a dodge they never had.
    canReact: could && !reactionDenied(ctx, shot, f),
    mods: attackerReactionMods(ctx, shot.isMelee, f),
  };
}

/** A victim struck: their frame, the hits resolved against them, and the volley they belong to. */
interface Strike {
  f: VictimFrame;
  hits: HitResult[];
  volley: Volley;
}

/**
 * The reaction prompt's payload. Declarative: one field per line, in the
 * order it has always been written.
 */
function pendingPayload(ctx: AttackContext, shot: Shot, { f, hits, volley }: Strike, mods: ReactionMods) {
  return {
    attackRoll: shot.attackRoll,
    modifiedSkill: shot.modifiedSkill,
    dos: shot.dos,
    dof: 0, // a hit has no degrees of failure
    hits,
    slot: shot.slot,
    effectiveMode: shot.effectiveMode,
    tabId: f.victim.tabId,
    totalHits: f.hits,
    specialNotes: f.notes,
    overheatDmg: f.isPrimary ? shot.overheatDmg : 0,
    haywireRoll: shot.haywireRoll,
    isMelee: shot.isMelee,
    // Null-coerced (gotcha #2): who struck, for the display's beat (plan map-display, D).
    actorId: ctx.actorId ?? null,
    // The kind and the rate, for the beat once the reactions are done (K1; the Track K review: lost before).
    beatKind: victimBeat(shot, f.victim),
    // Null-coerced (gotcha #2): who fired, for a strict exposure scope (v8.368.4) - the tag and, since 2026-10-23,
    // the side; the reaction path dropped both until then, so a reaction's hits read the attacker as teamless.
    attackerTeamTag: ctx.attackerTeamTag ?? null,
    attackerSideTag: ctx.attackerSideTag ?? null,
    attackerExposures: shot.attackerExposures,
    attackerAuras: ctx.actorAuras,
    // Null-coerced (gotcha #2): the template as this shot rolled it (v8.372.4).
    areaRolled: shot.rolledArea ?? null,
    cdResult: f.isPrimary ? shot.cdResult : null,
    // Coerced: `undefined` in any field kills the whole Firestore defence
    // write (gotcha #2), and this one has been poisoned once.
    furAssaultEligible: !!(f.isPrimary && shot.furAssaultEligible),
    // Null-coerced (gotcha #2): the attacker's reaction exposures (v8.368.3).
    attackerReactionMods: mods,
    skillBreakdown: shot.skillBreakdown,
    // Null for an ordinary attack; shared when one shot owes several
    // reactions, so the log can group them. (Distinct from `attackGroupId`
    // in useApplyHits, which groups one combatant's own entries.)
    volleyId: volley.id,
    // How many this shot caught - each line must say what it was part of on its own.
    volleySize: volley.size,
  };
}

/**
 * The pending-defence document: the prompt, who fired, and the target
 * frozen as it enters defence. The defender's custom modifiers are folded
 * in HERE, once, at the moment the target enters defence - mirroring what
 * `defenceProbability` does at its own entry point. Neither
 * `handleDefenceChoice` nor `getAvailableDefenceSteps` reads them, and
 * `projectDefender` deliberately does not, so before this a defender
 * modifier showed in the forecast and never in the roll. `withDefenderMods`
 * empties the list as it folds, so nothing downstream applies them twice.
 */
function defenceDocument(ctx: AttackContext, local: Record<string, unknown>, target: Target, mods: ReactionMods) {
  return {
    ...local,
    attackerId: ctx.playerId || 'unknown',
    attackerName: ctx.user?.sessionName || ctx.user?.displayName || 'Unknown',
    createdAt: Date.now(),
    frozenTarget: { ...withDefenderMods(deepClone(target)), attackerReactionMods: mods },
    resolvedBy: null,
    resolvedAt: null,
    choice: null,
  };
}

/** Owe the victim a reaction: the attacker's local prompt (primary only) and a pending-defence entry. */
function promptDefence(ctx: AttackContext, shot: Shot, strike: Strike, mods: ReactionMods): void {
  const localPendingData = pendingPayload(ctx, shot, strike, mods);
  // The attacker's own prompt is the PRIMARY victim's, so the beat before
  // the document lands still shows one thing, not five.
  if (strike.f.isPrimary) ctx.setPendingAttack(localPendingData);
  // EVERY victim gets an entry, session or not - the only way solo can owe
  // more than one reaction at a time (v8.154.0).
  if (ctx.createPendingDefence) {
    ctx.createPendingDefence(strike.f.victim.tabId, defenceDocument(ctx, localPendingData, strike.f.target, mods));
  }
}

/**
 * The beat's facts for one victim: the shot's kind and rate, and the blast template that caught them when there is
 * one (T2) - spread only then, since the defence write refuses `undefined` (gotcha #2).
 */
const victimBeat = (shot: Shot, victim: AttackVictim): BeatKindFacts =>
  victim.area ? { ...shot.beatKind, area: victim.area } : shot.beatKind;

/**
 * The applyHits payload for hits nobody reacts to. Declarative: one field
 * per line, in the order it has always been written.
 */
function directPayload(ctx: AttackContext, shot: Shot, { f, hits, volley }: Strike): ApplyHitsData {
  return {
    attackRoll: shot.attackRoll,
    modifiedSkill: shot.modifiedSkill,
    dos: shot.dos,
    dof: 0, // a hit has no degrees of failure
    hits,
    hitsAfterDefence: hits,
    slot: shot.slot,
    fireMode: shot.effectiveMode,
    dodgeResult: null,
    parryResult: null,
    totalHits: f.hits,
    specialNotes: f.notes,
    overheatDmg: f.isPrimary ? shot.overheatDmg : 0,
    haywireRoll: shot.haywireRoll,
    actorId: ctx.actorId ?? null,
    beatKind: victimBeat(shot, f.victim),
    attackerTeamTag: ctx.attackerTeamTag,
    attackerSideTag: ctx.attackerSideTag,
    attackerExposures: shot.attackerExposures,
    attackerAuras: ctx.actorAuras,
    // Null-coerced (gotcha #2): the template as this shot rolled it (v8.372.4).
    areaRolled: shot.rolledArea ?? null,
    cdResult: f.isPrimary ? shot.cdResult : null,
    skillBreakdown: shot.skillBreakdown,
    volleyId: volley.id,
    volleySize: volley.size,
  };
}

/** No reaction owed: the hits land now, and the primary may earn Furious Assault. */
function applyDirect(ctx: AttackContext, shot: Shot, strike: Strike): void {
  ctx.applyHits(strike.f.victim.tabId, directPayload(ctx, shot, strike));
  // Phase 7: after a direct applyHits, the Furious Assault prompt if eligible.
  if (strike.f.isPrimary && shot.furAssaultEligible) {
    ctx.setPendingBonusAttack({
      talentName: 'Furious Assault',
      slot: shot.slot,
      tabId: shot.tabId,
      modifiedSkill: shot.modifiedSkill,
      actorId: ctx.actorId ?? null,
    });
  }
}

/**
 * Resolve the shot against one victim. Returns whether the template CAUGHT
 * them - false only for a victim who stepped clear of it (or has no target
 * to resolve against), which is what decides whether they spawn the
 * secondary (v8.373.2).
 */
export function resolveAgainstVictim(ctx: AttackContext, shot: Shot, victim: AttackVictim, volley: Volley): boolean {
  const f = frameVictim(ctx, shot, victim);
  if (!f) return false;
  const avoidScale = rollAvoid(ctx, shot, f, volley);
  if (avoidScale === null) return false;
  const { hits } = resolveHitsAgainst(hitsRequest(ctx, shot, f, avoidScale));
  const gate = reactionGate(ctx, shot, f);
  const strike = { f, hits, volley };
  if (gate.canPrompt && gate.canReact && hits.some((h) => !h.ffBlocked)) promptDefence(ctx, shot, strike, gate.mods);
  else applyDirect(ctx, shot, strike);
  return true;
}
